﻿import React, { useState, useEffect } from 'react';
import { Ticket } from '../models/ticket';
import { LookupItem } from '../models/lookup-item';
import { TicketDetail } from '../models/ticket-detail';
import { TicketContext } from '../models/ticket-context';
import { CrudFunctions } from '../utilities/crud-functions';
import { EditorConstants } from '../store/editor-constants';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import { TextBox, SelectBox } from 'devextreme-react';
import { Button } from 'devextreme-react/button';
import HtmlEditor, {
    Toolbar, MediaResizing, Item
} from 'devextreme-react/html-editor';

export interface Props {
    contextData?: any;
    formName?: string;
    detailOnly?: boolean;
    onApplyDetail?: (detail: TicketDetail) => void;
    onComplete?: () => void;
    onCancel?: () => void;
}

export default function TicketLogEntryForm(props: Props) {
    const [ticket, setTicket] =
        useState<Ticket>();

    const [editorContent, setEditorContent] =
        useState<string>();

    const [ticketTypes, setTicketTypes] =
        useState<LookupItem[]>();

    const [ticketSeverities, setTicketSeverities] =
        useState<LookupItem[]>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    return (
        <div id="TicketLogDialogContainer">

            {!props.detailOnly &&
                <div>
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Ticket Type</div>
                            <SelectBox className="selectbox"
                                placeholder='NA' displayExpr={'name'} valueExpr={'id'}
                                dataSource={ticketTypes ? ticketTypes : []}
                                onValueChanged={onTicketTypeSelectionChanged}
                                value={ticket && ticket.ticketType ?
                                    ticket.ticketType : null} />
                        </div>

                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Ticket Severity</div>
                            <SelectBox className="selectbox"
                                placeholder='NA' displayExpr={'name'} valueExpr={'id'}
                                dataSource={ticketSeverities ? ticketSeverities : []}
                                onValueChanged={onTicketSeveritySelectionChanged}
                                value={ticket && ticket.severity ?
                                    ticket.severity : null} />
                        </div>
                    </div>

                    <div className="pw-form-unit">
                        <div className="pw-form-unit-label">Description</div>
                        <TextBox className="textbox"
                            onValueChanged={onDescriptionValueChanged}
                            showClearButton={true}
                            placeholder="description"
                            value={ticket && ticket.description ?
                                ticket.description : undefined} />
                    </div>
                </div>
            }

            <div id="TicketDialogHtmlEditor" className="pw-margin-left-05 pw-margin-right-05">
                <HtmlEditor height="100%"
                    value={editorContent ? editorContent : undefined}
                    onValueChanged={onEditorContentChanged}>
                    <MediaResizing enabled={true} />

                    <Toolbar multiline={false} >
                        <Item name="separator" />
                        <Item name="undo" />
                        <Item name="redo" />
                        <Item name="separator" />
                        <Item name="size" acceptedValues={EditorConstants.FontSizes} />
                        <Item name="separator" />
                        <Item name="bold" />
                        <Item name="italic" />
                        <Item name="strike" />
                        <Item name="underline" />
                        <Item name="separator" />
                        <Item name="color" />
                        <Item name="background" />
                    </Toolbar>
                </HtmlEditor>
            </div>

            <div id="TicketDialogButtons" className="pw-comp-row pw-margin-top-05">
                <div className="pw-centered-buttons-container">
                    <Button id="submitButton"
                        className="pw-button"
                        text="Submit" type="success"
                        hint="submit ticket"
                        height={'2.6em'}
                        onClick={onSubmitClick} />

                    <Button id="cancelButton"
                        className="pw-button"
                        text="Cancel" type="normal"
                        hint="cancel"
                        height={'2.6em'}
                        onClick={onCancelClick} />
                </div>
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onEditorContentChanged(e: any) {
        //this is needed because what seems to be a DE bug- if the event is undefined/null then it is not
        // a legit event firing
        if (!e.event)
            return;

        //console.log("onEditorContentChanged");
        //console.log(e);

        setEditorContent(e.value);
    }

    function onTicketTypeSelectionChanged(e: any) {
        setTicket({ ...ticket, ticketType: e.value });
    }

    function onTicketSeveritySelectionChanged(e: any) {
        setTicket({ ...ticket, severity: e.value });
    }

    function onDescriptionValueChanged(e: any) {
        setTicket({ ...ticket, description: e.value });
    }

    function onSubmitClick() {
        if (editorContent) {
            if (props.detailOnly && props.onApplyDetail) {
                const detail: TicketDetail = {
                    content: editorContent
                };

                props.onApplyDetail(detail)
                return;
            }
            else if (ticket) {
                const editedTicket = { ...ticket };

                const details: TicketDetail[] = [
                    { content: editorContent }
                ];

                editedTicket.details = details;

                if (props.contextData) {
                    const context: TicketContext = {
                        contentJson: JSON.stringify(props.contextData)
                    };

                    editedTicket.context = context;
                }

                submitTicket(editedTicket)
                    .then(() => {
                        onComplete();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Submitting Ticket");
                    });
            }
        }
    }

    function onComplete() {
        if (props.onComplete)
            props.onComplete();
    }

    function onCancelClick() {
        if (props.onCancel)
            props.onCancel();
    }

    //GET 

    async function getTicketTypes() {
        await CrudFunctions.getData("support/tickettypes")
            .then((data) => {
                setTicketTypes(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Ticket Types");
            });
    }

    async function getTicketSeverities() {
        await CrudFunctions.getData("support/ticketseverities")
            .then((data) => {
                setTicketSeverities(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Ticket Severities");
            });
    }

    // POST

    async function submitTicket(ticket: Ticket) {
        return await CrudFunctions.postData("support/ticket", ticket);
    }

    async function getAllData() {
        if (props.detailOnly)
            return;

        await Promise.all([
            getTicketTypes(),
            getTicketSeverities()
        ]);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}

